import { useEffect, useState } from "react";
import { useLive } from "../lib/live";
import { silentPrint, desktop, getPrinters } from "../lib/desktopPrint";
import { fmtDT } from "../lib/units";
import { Plus, X, Check, Loader2, Printer, Users, ArrowRightLeft, Combine, Trash2, CreditCard, Pencil, ChefHat, UserCog, ShoppingCart, Clock3 } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { useAuth } from "../auth/AuthContext";
import { KotModifyModal, TabHistory } from "./KotModify";

const canManageFloor = (role) => ["owner", "admin", "manager"].includes(role || "owner");

const STATUS_COLORS = { free: "free", occupied: "occupied", billed: "billed" };

const sinceMin = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)) : 0);

export function TableSummary({ s }) {
  if (!s) return null;
  return (
    <span className="table-sum" data-testid="table-summary">
      <em><Clock3 size={10}/> {sinceMin(s.opened_at)}m</em>
      <em>{s.items} items · {money(s.amount)}</em>
      {s.carried_over ? <em className="k-done" data-testid={`table-carried-${s.tab_id}`}>open from an earlier day — bill or close</em>
        : s.kots > 0 && <em className={s.kots_new ? "k-new" : s.kots_ready ? "k-ready" : "k-done"}>{s.kots_new ? `${s.kots_new} KOT cooking` : s.kots_ready ? `${s.kots_ready} KOT ready` : "all served"}</em>}
      {s.pending > 0 && <em className="k-pend">{s.pending} not sent</em>}
      {s.bill && <em className="k-ready" data-testid={`table-bill-${s.tab_id}`}>{s.bill.payment_status === "partial" ? `part paid · ${money(s.bill.balance_due)} due` : `bill printed · unpaid`}</em>}
      {s.ready_to_bill && <em className="k-bill">READY TO BILL</em>}
    </span>
  );
}

export function TableMap({ tables, onOpen, onAdd, onEdit, onDelete, onWaiters, canManage, title = "Tables & KOT", subtitle = "Live floor status: occupancy, KOT progress and who is serving. Orders are punched in POS & Sales." }) {
  const bySection = {};
  for (const t of tables) { (bySection[t.section || "Hall"] = bySection[t.section || "Hall"] || []).push(t); }
  return (
    <>
      <div className="recipes-head">
        <div><span className="overline">FLOOR MAP</span><h2 style={{fontSize:20,margin:"6px 0 2px"}}>{title}</h2><p style={{margin:0,color:"var(--muted)",fontSize:13}}>{subtitle}</p></div>
        {canManage && <div style={{display:"flex",gap:8}}>
          <button className="secondary-button" onClick={onWaiters} data-testid="manage-waiters-button"><UserCog size={14}/> Manage waiters</button>
          <button className="primary-button" onClick={onAdd} data-testid="add-table-button"><Plus size={16}/> Add table</button>
        </div>}
      </div>
      {tables.length === 0 && <div className="panel" style={{padding:36,textAlign:"center",color:"var(--muted)"}} data-testid="tables-empty">No tables yet. Add your first table to start taking dine-in orders.</div>}
      {Object.entries(bySection).map(([section, list]) => (
        <section key={section} style={{marginBottom:22}}>
          <span className="overline" style={{display:"block",margin:"8px 0 10px"}}>{section}</span>
          <div className="table-grid">
            {list.map(t => (
              <div key={t.id} className={`table-tile ${STATUS_COLORS[t.status] || "free"} ${t.tab_summary?.ready_to_bill ? "ready-bill" : ""}`} data-testid={`table-tile-${t.id}`}>
                <button className="table-main" onClick={() => onOpen(t)} data-testid={`table-open-${t.id}`}>
                  <b>{t.name || `Table ${t.number}`}</b>
                  <small><Users size={10}/> {t.capacity}{t.tab_summary?.waiter_name ? ` · ${t.tab_summary.waiter_name}` : ""}</small>
                  <span className="table-status" data-testid={`table-status-${t.id}`}>{t.status === "occupied" ? "OCCUPIED" : "FREE"}</span>
                  <TableSummary s={t.tab_summary}/>
                </button>
                {canManage && <div className="table-actions">
                  <button className="icon-button" onClick={() => onEdit(t)} title="Edit" data-testid={`edit-table-${t.id}`}><Pencil size={13}/></button>
                  {t.status === "free" && <button className="icon-button" onClick={() => onDelete(t)} title="Delete" data-testid={`delete-table-${t.id}`}><Trash2 size={13}/></button>}
                </div>}
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function TableForm({ initial, onCancel, onSave, busy, error }) {
  const [form, setForm] = useState(() => ({ number: initial?.number || "", section: initial?.section || "Hall", capacity: initial?.capacity || 4, name: initial?.name || "" }));
  const upd = (k,v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, capacity: Number(form.capacity) || 4 }); }}>
      <div className="form-grid">
        <label>Table number<input value={form.number} onChange={e => upd("number", e.target.value)} required placeholder="e.g. 7" data-testid="table-number-input"/></label>
        <label>Section<input value={form.section} onChange={e => upd("section", e.target.value)} placeholder="e.g. Hall / Terrace / AC"/></label>
        <label>Capacity<input type="number" min="1" value={form.capacity} onChange={e => upd("capacity", e.target.value)}/></label>
        <label>Nickname (optional)<input value={form.name} onChange={e => upd("name", e.target.value)} placeholder="e.g. Window 1"/></label>
      </div>
      {error && <div className="auth-alert error">{error}</div>}
      <div className="setup-actions" style={{marginTop:12}}>
        <button type="button" className="secondary-button" onClick={onCancel}>Cancel</button>
        <span style={{flex:1}}/>
        <button className="primary-button" disabled={busy || !form.number.trim()} data-testid="save-table-button">{busy ? <Loader2 className="spin" size={14}/> : <Check size={14}/>} Save</button>
      </div>
    </form>
  );
}

export function TabPanel({ tab, recipes, tables, waiters, branch, onBack, onChanged, mode = "order", onGoPos, onGoBill }) {
  const ordering = mode === "order";
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [kot, setKot] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [waiterId, setWaiterId] = useState(tab.waiter_id || "");
  const [modIdx, setModIdx] = useState(null);
  const role = useAuth()?.user?.role;

  const items = tab.items || [];
  const subtotal = items.reduce((a, b) => a + b.qty * b.unit_price, 0);
  const gstTotal = items.reduce((a, b) => a + (b.qty * b.unit_price * (b.gst_rate || 5)) / 100, 0);
  const pendingCount = items.filter(i => i.status === "pending").length;
  const activeWaiters = (waiters || []).filter(w => w.active !== false);

  const setWaiter = async (id) => {
    setWaiterId(id);
    try { await api.put(`/tabs/${tab.id}/waiter`, { waiter_id: id }); await onChanged(); }
    catch (e) { setErr(errorMessage(e)); }
  };

  const add = async (r) => {
    setBusy("add");
    try { await api.post(`/tabs/${tab.id}/items`, { recipe_id: r.id, name: r.name, qty: 1, unit_price: r.selling_price, gst_rate: r.gst_rate ?? 5, hsn_sac: r.hsn_sac || "2106" }); await onChanged(); }
    catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };
  const remove = async (it, idx) => {
    setBusy(`rm-${idx}`);
    try { await api.delete(`/tabs/${tab.id}/items/${it.item_id}`); await onChanged(); }
    catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };
  const sendKOT = async () => {
    setBusy("kot"); setErr("");
    const p = getPrinters(), desk = desktop() && p.kot_printer;
    const w = desk ? null : window.open("", "_blank", "width=380,height=640");
    try { const { data } = await api.post(`/tabs/${tab.id}/send-kot`); if (!desk) writeKot(w, data); else if (!p.auto_kot && !(await silentPrint("kot", kotHtml(data)))) setErr("KOT saved, but the KOT printer didn't respond — reprint it from KOT History"); setKot(data); await onChanged(); }
    catch (e) { w?.close(); setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };
  const transfer = async (target_table_id) => {
    setBusy("tx");
    try { await api.post(`/tabs/${tab.id}/transfer`, { target_table_id }); setTransferOpen(false); await onChanged(); }
    catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };
  const merge = async (source_tab_id) => {
    setBusy("mg");
    try { await api.post(`/tabs/${tab.id}/merge`, { source_tab_id }); setMergeOpen(false); await onChanged(); }
    catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };

  const grouped = {};
  for (const r of recipes) { if (r.available === false) continue; (grouped[r.category] = grouped[r.category] || []).push(r); }

  const freeTables = tables.filter(t => t.id !== tab.table_id && t.status === "free");
  const otherTabs = tables.filter(t => t.id !== tab.table_id && t.status === "occupied" && t.current_tab_id);

  return (
    <>
      <div className="recipes-head">
        <div><button className="secondary-button" onClick={onBack} data-testid="back-to-tables">← {ordering ? "Change table" : "Back to floor"}</button></div>
        <div><h2 style={{fontSize:20,margin:0}}>{tab.table_label}</h2><p style={{margin:0,color:"var(--muted)",fontSize:12}}>{items.length} items · {pendingCount} pending KOT</p></div>
        <div style={{display:"flex",gap:8}}>
          <button className="secondary-button" onClick={() => setTransferOpen(true)} data-testid="transfer-button" disabled={freeTables.length === 0}><ArrowRightLeft size={14}/> Transfer</button>
          <button className="secondary-button" onClick={() => setMergeOpen(true)} data-testid="merge-button" disabled={otherTabs.length === 0}><Combine size={14}/> Merge</button>
          {!ordering && onGoPos && <button className="secondary-button" onClick={() => onGoPos(tab)} data-testid="monitor-go-pos"><ShoppingCart size={14}/> Punch order in POS</button>}
          {onGoBill && items.length > 0 && <button className="primary-button" onClick={() => onGoBill(tab)} data-testid="go-final-bill"><CreditCard size={14}/> Final bill</button>}
        </div>
      </div>

      <div className={ordering ? "tab-layout" : "tab-layout monitor"}>
        {ordering && <div className="tab-menu panel">
          <div className="panel-head"><div><span className="overline">PICK FROM MENU</span><h2>Add dishes</h2></div></div>
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="pos-cat">
              <span className="overline">{cat}</span>
              <div className="pos-grid">
                {list.map(r => (
                  <button key={r.id} className="pos-tile" onClick={() => add(r)} disabled={busy === "add"} data-testid={`tab-add-${r.id}`}>
                    <b>{r.name}</b>
                    <span className="muted-label">GST {r.gst_rate ?? 5}%</span>
                    <strong>{money(r.selling_price)}</strong>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>}

        <aside className="tab-side panel">
          <div className="panel-head"><div><span className="overline">CURRENT TAB</span><h2><ChefHat size={16} style={{verticalAlign:"-3px"}}/> {tab.table_label}</h2></div></div>
          <label className="pos-field">Served by (waiter)
            <select value={waiterId} onChange={e => setWaiter(e.target.value)} disabled={!ordering} data-testid="tab-waiter-select">
              <option value="">— Pick waiter —</option>
              {activeWaiters.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <div className="pos-lines">
            {items.length === 0 && <div className="empty small">Nothing added yet</div>}
            {!items.some((it) => it.status !== "cancelled") && <button className="secondary-button" onClick={async () => { try { await api.post(`/tabs/${tab.id}/close-empty`); await onChanged(); onBack?.(); } catch (e) { setErr(errorMessage(e)); } }} data-testid="close-empty-tab">Free this table (nothing to bill)</button>}
            {items.map((it, i) => (
              <div className={`pos-line ${it.status === "cancelled" ? "is-cancelled" : ""}`} key={it.item_id || i} data-testid={`tab-line-${i}`}>
                <div>
                  <b>{it.name} {it.modified && <small className="pill mod-badge" data-testid={`tab-item-modified-${i}`}>modified</small>} <small className={it.status === "pending" ? "pill accent" : it.status === "cancelled" ? "pill mod-cancel" : "pill success"} data-testid={`tab-item-status-${i}`}>{it.status === "pending" ? "to kitchen" : it.status}</small></b>
                  <span>{it.qty} × {money(it.unit_price)} · GST {it.gst_rate}%{it.notes ? ` · ${it.notes}` : ""}</span>
                </div>
                <strong>{money(it.qty * it.unit_price)}</strong>
                {ordering && it.status === "pending" && <button className="icon-button" onClick={() => remove(it, i)} disabled={busy===`rm-${i}`} data-testid={`tab-remove-${i}`}><X size={13}/></button>}
                {ordering && !["pending", "cancelled"].includes(it.status) && <button className="icon-button" onClick={() => setModIdx(i)} title="Modify KOT item" data-testid={`tab-modify-${i}`}><Pencil size={13}/></button>}
              </div>
            ))}
          </div>
          <div className="pos-totals">
            <div><span>Subtotal</span><b>{money(subtotal)}</b></div>
            <div className="pos-grand"><span>Approx. with GST</span><strong>{money(subtotal + gstTotal)}</strong></div>
          </div>
          {err && <div className="auth-alert error">{err}</div>}
          {ordering && <button className="primary-button full kot-big" onClick={sendKOT} disabled={pendingCount === 0 || busy === "kot"} data-testid="send-kot-button">{busy === "kot" ? <Loader2 className="spin" size={16}/> : <Printer size={16}/>} Send &amp; Print KOT ({pendingCount})</button>}
        </aside>
      </div>

      <TabHistory tab={tab} branch={branch}/>
      {modIdx !== null && <KotModifyModal tab={tab} idx={modIdx} role={role} onClose={() => setModIdx(null)} onDone={async (d) => { setModIdx(null); setKot(d.mod_kot); await onChanged(); }}/>}
      {kot && <KOTPrint kot={kot} onClose={() => setKot(null)}/>}
      {transferOpen && (
        <div className="modal-backdrop"><div className="modal" data-testid="transfer-modal">
          <div className="modal-head"><div><span className="overline">TRANSFER TAB</span><h2>Move to another table</h2></div><button className="icon-button" onClick={() => setTransferOpen(false)}><X size={18}/></button></div>
          {freeTables.length === 0 && <div className="empty">No free tables available.</div>}
          <div className="table-grid">
            {freeTables.map(t => (
              <button key={t.id} className="table-tile free" onClick={() => transfer(t.id)} data-testid={`transfer-to-${t.id}`}>
                <div className="table-main"><b>{t.name || `Table ${t.number}`}</b><small>{t.section}</small></div>
              </button>
            ))}
          </div>
        </div></div>
      )}
      {mergeOpen && (
        <div className="modal-backdrop"><div className="modal" data-testid="merge-modal">
          <div className="modal-head"><div><span className="overline">MERGE TAB</span><h2>Combine with another table</h2></div><button className="icon-button" onClick={() => setMergeOpen(false)}><X size={18}/></button></div>
          {otherTabs.length === 0 && <div className="empty">No other occupied tables to merge.</div>}
          <div className="table-grid">
            {otherTabs.map(t => (
              <button key={t.id} className="table-tile occupied" onClick={() => merge(t.current_tab_id)} data-testid={`merge-from-${t.id}`}>
                <div className="table-main"><b>{t.name || `Table ${t.number}`}</b><small>{t.section}</small></div>
              </button>
            ))}
          </div>
        </div></div>
      )}
    </>
  );
}

const MOD_TXT = { increase: (i) => `ADD +${i.qty} (now ${i.new_qty})`, reduce: (i) => `REDUCE to ${i.new_qty} (was ${i.old_qty})`, cancel: (i) => `CANCEL (was ${i.old_qty})`, note: () => "NOTE CHANGE" };
const kotQty = (i) => (i.change ? (i.change === "increase" ? `+${i.qty}` : i.change === "cancel" ? "✕" : i.qty) : i.qty);
const kotSub = (i) => [i.change && MOD_TXT[i.change](i), i.notes, i.reason && `Reason: ${i.reason}`].filter(Boolean).join(" · ");

export function kotHtml(kot) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${kot.kot_no}</title><style>
      body{font-family:monospace;padding:10px;color:#000}
      h2{text-align:center;margin:4px 0;border-top:1px dashed #000;border-bottom:1px dashed #000;padding:6px 0}
      p{margin:3px 0;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:8px;font-size:inherit}
      td{padding:4px 2px;border-bottom:1px dashed #999;font-size:12px}
      .big{font-size:18px;font-weight:bold}
    </style></head><body>
      <h2>${kot.type === "modification" ? "** MODIFIED KOT **<br>" : ""}${kot.kot_no}</h2>
      ${kot.ref_kot_no ? `<p><b>Ref:</b> ${kot.ref_kot_no} · <b>By:</b> ${kot.modified_by || ""}</p>` : ""}
      <p><b>Table:</b> ${kot.table_label}</p>
      ${kot.waiter_name ? `<p><b>Waiter:</b> ${kot.waiter_name}</p>` : ""}
      <p><b>Time:</b> ${fmtDT(kot.printed_at)}</p>
      <table><tbody>${kot.items.map(i => `<tr><td class="big">${kotQty(i)}x</td><td>${i.name}${kotSub(i) ? "<br><small>"+kotSub(i)+"</small>" : ""}</td></tr>`).join("")}</tbody></table>
      <p style="text-align:center;margin-top:12px;font-size:10px">— KITCHEN COPY —</p>
    </body></html>`;
}

export function writeKot(w, kot) {
    if (!w) return;
    w.document.write(kotHtml(kot));
    w.document.close(); w.focus(); w.print();
}

function KOTPrint({ kot, onClose }) {
  const print = async () => { if (!(await silentPrint("kot", kotHtml(kot)))) writeKot(window.open("", "_blank", "width=380,height=640"), kot); };
  return (
    <div className="modal-backdrop">
      <div className="modal kot-preview" data-testid="kot-preview">
        <div className="modal-head"><div><span className="overline">KITCHEN ORDER TICKET</span><h2>{kot.kot_no}</h2></div><button className="icon-button" onClick={onClose}><X size={18}/></button></div>
        <div className="kot-slip">
          {kot.type === "modification" && <p className="kot-mod-flag" data-testid="kot-preview-modified">MODIFIED KOT · ref {kot.ref_kot_no} · by {kot.modified_by}</p>}
          <h3>{kot.kot_no}</h3>
          <p><b>Table:</b> {kot.table_label}</p>
          {kot.waiter_name && <p><b>Waiter:</b> {kot.waiter_name}</p>}
          <p><b>Time:</b> {fmtDT(kot.printed_at)}</p>
          <table><tbody>{kot.items.map((i, idx) => (<tr key={idx}><td className="big">{kotQty(i)}×</td><td>{i.name}{kotSub(i) && <><br/><small>{kotSub(i)}</small></>}</td></tr>))}</tbody></table>
          <p style={{textAlign:"center",marginTop:12,fontSize:10}}>— KITCHEN COPY —</p>
        </div>
        <div className="setup-actions"><span style={{flex:1}}/><button className="primary-button" onClick={print} data-testid="print-kot-button"><Printer size={14}/> Print</button></div>
      </div>
    </div>
  );
}

export default function TablesView({ branch, recipes, onBillCreated, onGoPos, onGoBill }) {
  const role = useAuth()?.user?.role;
  const [tables, setTables] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [modal, setModal] = useState(null);
  const [waiterOpen, setWaiterOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [t, w] = await Promise.all([api.get(`/tables?branch=${branch}`), api.get(`/waiters?branch=${branch}`)]);
      setTables(t.data); setWaiters(w.data);
    } catch { /* noop */ }
  };
  const reloadTab = async () => {
    if (!activeTab) return;
    try { const { data } = await api.get(`/tabs/${activeTab.id}`); setActiveTab(data); await load(); }
    catch { setActiveTab(null); await load(); }
  };
  useEffect(() => { load(); }, [branch]); // eslint-disable-line

  useEffect(() => { const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [branch]); // eslint-disable-line
  useLive(() => load());
  const openTable = async (t) => {
    if (t.status !== "occupied" || !t.current_tab_id) { onGoPos?.(t); return; }
    setBusy(true);
    try { const { data } = await api.get(`/tabs/${t.current_tab_id}`); setActiveTab(data); }
    catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const saveTable = async (form) => {
    setBusy(true); setErr("");
    try {
      if (modal.initial?.id) { await api.put(`/tables/${modal.initial.id}`, form); }
      else { await api.post(`/tables?branch=${branch}`, form); }
      setModal(null); await load();
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const removeTable = async (t) => {
    if (!window.confirm(`Delete ${t.name || "Table " + t.number}?`)) return;
    try { await api.delete(`/tables/${t.id}`); await load(); }
    catch (e) { alert(errorMessage(e)); }
  };

  if (activeTab) {
    return <TabPanel mode="monitor" tab={activeTab} recipes={recipes} tables={tables} waiters={waiters} branch={branch} onBack={() => { setActiveTab(null); load(); }} onChanged={reloadTab} onGoPos={(tb) => onGoPos?.({ id: tb.table_id })} onGoBill={onGoBill}/>;
  }

  return (
    <>
      <TableMap canManage={canManageFloor(role)} tables={tables} onOpen={openTable} onAdd={() => setModal({ initial: null })} onEdit={(t) => setModal({ initial: t })} onDelete={removeTable} onWaiters={() => setWaiterOpen(true)}/>
      {err && <div className="auth-alert error" style={{marginTop:14}}>{err}</div>}
      {modal && (
        <div className="modal-backdrop">
          <div className="modal" data-testid="table-modal">
            <div className="modal-head"><div><span className="overline">TABLE</span><h2>{modal.initial ? "Edit table" : "Add table"}</h2></div><button className="icon-button" onClick={() => setModal(null)}><X size={18}/></button></div>
            <TableForm initial={modal.initial} onCancel={() => setModal(null)} onSave={saveTable} busy={busy} error={err}/>
          </div>
        </div>
      )}
      {waiterOpen && <WaiterManager branch={branch} waiters={waiters} onClose={() => setWaiterOpen(false)} onChanged={load}/>}
    </>
  );
}

function WaiterManager({ branch, waiters, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const add = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post(`/waiters?branch=${branch}`, { name: name.trim(), phone: phone.trim(), active: true }); setName(""); setPhone(""); await onChanged(); }
    catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const remove = async (w) => {
    if (!window.confirm(`Delete waiter ${w.name}?`)) return;
    try { await api.delete(`/waiters/${w.id}`); await onChanged(); }
    catch (e) { alert(errorMessage(e)); }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" data-testid="waiter-manager">
        <div className="modal-head"><div><span className="overline">STAFF</span><h2>Waiters at this branch</h2></div><button className="icon-button" onClick={onClose}><X size={18}/></button></div>
        <form onSubmit={add}>
          <div className="form-grid">
            <label>Full name<input value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Ramesh Yadav" data-testid="waiter-name-input"/></label>
            <label>Phone (optional)<input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Enter 10-digit mobile number" data-testid="waiter-phone-input"/></label>
          </div>
          {err && <div className="auth-alert error">{err}</div>}
          <button className="primary-button full" disabled={busy || !name.trim()} data-testid="save-waiter-button"><Plus size={14}/> Add waiter</button>
        </form>
        <div style={{marginTop:16}}>
          <span className="overline" style={{display:"block",marginBottom:8}}>Current team ({waiters.length})</span>
          {waiters.length === 0 && <div className="empty small">No waiters added yet.</div>}
          <div className="waiter-list">
            {waiters.map(w => (
              <div key={w.id} className="waiter-row" data-testid={`waiter-row-${w.id}`}>
                <div><b>{w.name}</b>{w.phone && <small>  ·  {w.phone}</small>}</div>
                <button className="icon-button" onClick={() => remove(w)} title="Delete" data-testid={`delete-waiter-${w.id}`}><Trash2 size={13}/></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
